import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MessagesSquare, CheckCircle2, XCircle, BookOpen, ArrowRight, ScrollText, Activity,
  Circle, Lock, ChevronLeft, Sparkles, Users, Inbox,
} from "lucide-react";
import { api } from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";
import { Panel, Tag, Button } from "@/components/ui";

const METRICS = [
  { key: "conversations", label: "Conversations", icon: MessagesSquare },
  { key: "customers", label: "Customers", icon: Users },
  { key: "actionsSucceeded", label: "Requests in Trello", icon: CheckCircle2 },
  { key: "requests", label: "Total requests", icon: Inbox },
];

function Dot({ ok, label, value }) {
  const color = value === "healthy" || value === "configured" || ok ? "var(--success)"
    : value === "unconfigured" ? "var(--text-muted)" : "var(--danger)";
  return (
    <div className="flex items-center justify-between py-2" style={{ borderBottom: "1px solid var(--border)" }}>
      <span className="text-sm" style={{ color: "var(--text-2)" }}>{label}</span>
      <span className="flex items-center gap-2 mono text-xs">
        <span style={{ width: 8, height: 8, borderRadius: 999, background: color }} />
        {value || (ok ? "ok" : "—")}
      </span>
    </div>
  );
}

function StepRow({ done, soon, title, desc, to, cta }) {
  const Icon = done ? CheckCircle2 : soon ? Lock : Circle;
  return (
    <div className="flex items-start gap-3 py-4" style={{ borderTop: "1px solid var(--border)" }}>
      <Icon size={20} strokeWidth={1.6} style={{ color: done ? "var(--accent-2)" : soon ? "var(--text-muted)" : "var(--text-2)", flexShrink: 0, marginTop: 1 }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ color: done ? "var(--text-2)" : "var(--text)", textDecoration: done ? "line-through" : "none" }}>{title}</span>
          {soon && <span className="mono text-[10px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>soon</span>}
        </div>
        <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{desc}</p>
      </div>
      {!done && !soon && to && (
        <Link to={to} className="btn btn-accent2 btn-sm" data-testid={`step-cta-${cta}`}>{cta} <ArrowRight size={13} /></Link>
      )}
      {done && <span className="mono text-[10px] uppercase tracking-widest" style={{ color: "var(--accent-2)" }}>done</span>}
    </div>
  );
}

function GettingStarted({ active, status }) {
  const o = active.onboarding || {};
  const profileDone = !!o.profileComplete;
  const instructionsDone = !!status?.activeVersion || !!o.playbookPublished;
  const done = (profileDone ? 1 : 0) + (instructionsDone ? 1 : 0);
  const total = 2;
  if (done >= total) return null;
  return (
    <Panel className="p-6 mb-6" data-testid="getting-started">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={16} style={{ color: "var(--accent-2)" }} />
        <span className="mono text-[11px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Getting started · {done} of {total}</span>
      </div>
      <h2 className="text-lg font-medium mb-2" style={{ color: "var(--text)" }}>A few steps and Wappy is ready</h2>
      <p className="text-sm mb-2" style={{ color: "var(--text-2)" }}>Do these in order. We'll guide you through each one.</p>
      <StepRow done={profileDone} title="Tell Wappy about your business" desc="Your name, what you do, hours, and how you like to talk to customers."
        to="/onboarding/business" cta="Set up" />
      <StepRow done={instructionsDone} title="Write Wappy's instructions" desc="In plain words: how to handle messages, what to ask, and when to call a person."
        to="/app/playbook" cta="Write" />
      <StepRow soon title="Add your info & FAQs" desc="Upload prices, policies, and answers so Wappy replies accurately." />
      <StepRow soon title="Connect your tools & WhatsApp" desc="Link Trello and your WhatsApp number to go live." />
    </Panel>
  );
}

export default function Dashboard() {
  const { active, loading } = useBusiness();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState(null);
  const [deps, setDeps] = React.useState(null);
  const [metrics, setMetrics] = React.useState(null);

  React.useEffect(() => {
    if (!active) return;
    api.get(`/businesses/${active.id}/playbook`).then((r) => setStatus(r.data)).catch(() => {});
    api.get(`/health/dependencies`).then((r) => setDeps(r.data)).catch(() => {});
    api.get(`/businesses/${active.id}/metrics`).then((r) => setMetrics(r.data)).catch(() => {});
  }, [active]);

  if (loading) return null;
  if (!active)
    return (
      <Panel className="p-8 text-center">
        <p className="text-sm mb-4" style={{ color: "var(--text-2)" }}>You don't have a workspace yet.</p>
        <Link to="/onboarding/business" className="btn btn-accent" data-testid="dashboard-create-ws">Create workspace <ArrowRight size={16} /></Link>
      </Panel>
    );

  return (
    <div>
      <button onClick={() => navigate("/app")} className="flex items-center gap-1.5 mb-4 text-sm" style={{ color: "var(--text-muted)" }} data-testid="back-to-workspaces">
        <ChevronLeft size={15} /> All workspaces
      </button>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="heading-eyebrow mb-1">{active.name}</div>
          <h1 className="text-xl font-semibold">Dashboard</h1>
        </div>
        <Tag tone={status?.activeVersion ? "accent" : "warning"} data-testid="agent-status">
          {status?.activeVersion ? "Agent active" : "Not live yet"}
        </Tag>
      </div>

      <GettingStarted active={active} status={status} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px mb-6" style={{ background: "var(--border)" }}>
        {METRICS.map((m, i) => (
          <motion.div key={m.key} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            className="p-5" style={{ background: "var(--s1)" }} data-testid={`metric-${m.key}`}>
            <m.icon size={16} style={{ color: "var(--text-muted)" }} />
            <div className="text-2xl font-semibold mt-3 mono">{metrics ? (metrics[m.key] ?? 0) : "—"}</div>
            <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{m.label}</div>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Panel className="p-5 lg:col-span-3">
          <div className="flex items-center gap-2 mb-3"><Activity size={16} style={{ color: "var(--accent)" }} /><span className="heading-eyebrow">Integration health</span></div>
          <div className="grid sm:grid-cols-2 gap-x-8">
            <div>
              <Dot label="MongoDB" value={deps?.mongodb} />
              <Dot label="Anthropic" value={deps?.anthropic} />
              <Dot label="Chroma" value={deps?.chroma} />
            </div>
            <div>
              <Dot label="Cognee" value={deps?.cognee} />
              <Dot label="Composio" value={deps?.composio} />
              <Dot label="WhatsApp" value={deps?.whatsapp} />
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}
